/**
 * TA-1194: the deterministic daily owner briefing (Agent Brains 5.7, "the Analyst
 * produces one"; apps/web agents.ts says "the daily briefing").
 *
 * A template over counts and ids. No model is called, and nothing here can carry
 * customer text: a section holds a count and internal ids (draft ids, thread
 * ids), and an id that is not shaped like one is refused, so a caller cannot
 * smuggle a name or a message in through the list (CLAUDE.md section 10).
 * It is an operator-facing summary. It states no offer, price or promise
 * (section 0.5) and nothing in it is sent to a customer.
 *
 * Sections are only those with a real reader in the codebase. G51 (D04 plan 2,
 * TA-1194 decides 4): appointments today and stage SLA breaches now have
 * readers, so both are sections. Appointments are read on the shop's own
 * calendar day, so with no shop time zone set that section is null and reads
 * "not measured", never zero and never a UTC guess; such a day is never quiet.
 */

import { PRICE_WARN_DAYS } from "./freshness";
import { DAY_MS, HOURS } from "./window";

/** The line the worker prints; it begins with the `[digest]` prefix the digest alarm family uses. */
export const BRIEFING_MAIL_PREFIX = "[digest] briefing";

/** Ids named per section; the rest read "+N more". A display length, not a limit on what is counted. */
export const BRIEFING_ID_CAP = 5;

/**
 * G51: how far back a `stage.sla_breached` row counts toward the morning's breaches, the gap row's "in 24h".
 */
// AWAITING RATIFICATION
export const BRIEFING_SLA_LOOKBACK_MS = DAY_MS;

export interface BriefingSection {
  /** The whole count, however many ids are listed. */
  readonly count: number;
  /** Up to the whole set; the builder shows `BRIEFING_ID_CAP`. */
  readonly ids: readonly string[];
}

export interface BriefingCounts {
  /** Drafts in `pending_approval`, waiting on the owner. */
  readonly draftsWaiting: BriefingSection;
  /** Meta threads whose 24 hour window is in its final 2 hours with the customer's message unanswered. */
  readonly windowsInGrace: BriefingSection;
  /** Threads where the shop still owes the customer an answer on a recorded promise. */
  readonly owedAnswers: BriefingSection;
  /** Threads with an open complaint no operator has closed. */
  readonly openComplaints: BriefingSection;
  /** Threads Routing handed to Scheduling that have no booked or confirmed appointment. */
  readonly schedulingNeeds: BriefingSection;
  /**
   * G51: threads with a booked or confirmed appointment on the shop's own calendar day. Null when the shop
   * has no time zone set: not measured, never 0 and never a UTC day.
   */
  readonly appointmentsToday: BriefingSection | null;
  /** G51: open threads with a `stage.sla_breached` row in the last `BRIEFING_SLA_LOOKBACK_MS`, counted once each. */
  readonly slaBreaches: BriefingSection;
}

/**
 * TA-1361: the KB's price dates, as `kbExpiry` (freshness.ts) counts them over the KB the worker serves. Counts
 * only: the ids are on the day's `kb_price_stale` alert row and the Shop answers page shows each fact's date.
 */
export interface BriefingKbPrices {
  readonly priced: number;
  /** Past their date: the reply path answers "let me check on that" instead of quoting them. */
  readonly stale: number;
  /** Within `PRICE_WARN_DAYS` of their date, still quoted. */
  readonly expiring: number;
}

export interface DailyBriefing {
  readonly day: string;
  readonly empty: boolean;
  /** The briefing, plain lines. */
  readonly text: string;
  /** The one line for the alert-mail path, counts only. */
  readonly mailLine: string;
}

/** An internal id: letters, digits, underscore, colon, dash-free. Never a sentence, never a name with spaces. */
export const BRIEFING_ID_SHAPE = /^[A-Za-z0-9_:.]{1,64}$/;
const ID_SHAPE = BRIEFING_ID_SHAPE;
const DAY_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

function check(name: string, s: BriefingSection): void {
  if (!Number.isInteger(s.count) || s.count < 0) throw new Error(`daily briefing: ${name} count must be a non-negative integer`);
  if (s.ids.length > s.count) throw new Error(`daily briefing: ${name} lists ${s.ids.length} ids but the count is ${s.count}; the count must cover them`);
  for (const id of s.ids) {
    if (!ID_SHAPE.test(id)) throw new Error(`daily briefing: ${name} carries an id that is not shaped like an internal id`);
  }
}

function line(label: string, s: BriefingSection): string {
  if (s.count === 0) return `${label}: none`;
  const shown = s.ids.slice(0, BRIEFING_ID_CAP);
  const more = s.count - shown.length;
  const parts = [...shown, ...(more > 0 && shown.length > 0 ? [`+${more} more`] : [])];
  return parts.length === 0 ? `${label}: ${s.count}` : `${label}: ${s.count} (${parts.join(", ")})`;
}

const APPOINTMENTS_LABEL = "Appointments today";
const SLA_LABEL = `Stage SLA breaches, last ${BRIEFING_SLA_LOOKBACK_MS / HOURS} hours`;

function checkKbPrices(p: BriefingKbPrices): void {
  for (const [name, n] of [["priced", p.priced], ["stale", p.stale], ["expiring", p.expiring]] as const) {
    if (!Number.isInteger(n) || n < 0) throw new Error(`daily briefing: the ${name} price count must be a non-negative integer`);
  }
  if (p.stale + p.expiring > p.priced) throw new Error("daily briefing: the stale and expiring price counts must fit inside the priced count");
}

export function buildDailyBriefing(input: {
  readonly day: string;
  readonly counts: BriefingCounts;
  /**
   * TA-1361: absent for a caller that serves no KB, and then no price line is printed, never a zero nobody
   * measured. The worker always passes it.
   */
  readonly kbPrices?: BriefingKbPrices;
}): DailyBriefing {
  if (!DAY_SHAPE.test(input.day)) throw new Error(`daily briefing: day must be YYYY-MM-DD, got ${JSON.stringify(input.day)}`);
  const c = input.counts;
  const kb = input.kbPrices;
  if (kb !== undefined) checkKbPrices(kb);
  // TA-1361: the briefing has no facts section, so the line follows Owed answers, the section nearest in meaning:
  // a price past its date is what turns a customer's price question into an answer the shop still owes.
  const kbLines =
    kb === undefined
      ? []
      : [`Prices on file: ${kb.priced} priced, ${kb.stale} past their date, ${kb.expiring} more within ${PRICE_WARN_DAYS} days`];
  const sections = [
    ["Drafts waiting on you", c.draftsWaiting],
    ["Windows in grace now", c.windowsInGrace],
    ["Owed answers", c.owedAnswers],
    ["Open complaints", c.openComplaints],
    ["Scheduling requests not yet booked", c.schedulingNeeds],
    [SLA_LABEL, c.slaBreaches],
  ] as const;
  const names = ["draftsWaiting", "windowsInGrace", "owedAnswers", "openComplaints", "schedulingNeeds", "slaBreaches"] as const;
  sections.forEach(([, s], i) => check(names[i]!, s));
  const appts = c.appointmentsToday;
  if (appts !== null) check("appointmentsToday", appts);

  // Not measured is not nothing: a day whose appointments could not be read is never called quiet.
  // TA-1361: the price counts never decide quiet. A stale price is already loud as its own kb_price_stale owner
  // alert, and counting it here would mail every day of a standing stale book and each of the 30 warning days.
  const empty = appts !== null && appts.count === 0 && sections.every(([, s]) => s.count === 0);
  const apptLine = appts === null ? `${APPOINTMENTS_LABEL}: not measured, the shop's time zone is not set` : line(APPOINTMENTS_LABEL, appts);
  const sectionLines = sections.slice(0, 5).map(([label, s]) => line(label, s));
  const text = empty
    ? `Daily briefing ${input.day}: nothing needs you today. No drafts waiting, no windows in grace, no owed answers, no open complaints, no unbooked scheduling requests, no appointments today and no stage SLA breaches.`
    : [`Daily briefing ${input.day}`, ...sectionLines.slice(0, 3), ...kbLines, ...sectionLines.slice(3), apptLine, line(SLA_LABEL, c.slaBreaches)].join("\n");
  // A quiet day is logged, never mailed: the digest alarm mails on any `[digest]` line, and a mail that says
  // nothing every day trains the owner to stop reading it. The day's digest.briefing row is written either way.
  const mailLine = empty
    ? `briefing day=${input.day} quiet: nothing needs you today (no mail)`
    : `${BRIEFING_MAIL_PREFIX} day=${input.day} drafts=${c.draftsWaiting.count} grace=${c.windowsInGrace.count}` +
      ` owed=${c.owedAnswers.count} complaints=${c.openComplaints.count} scheduling=${c.schedulingNeeds.count}` +
      ` appointments=${appts === null ? "not_measured" : appts.count} sla=${c.slaBreaches.count}` +
      (kb === undefined ? "" : ` prices_stale=${kb.stale} prices_expiring=${kb.expiring}`);
  return { day: input.day, empty, text, mailLine };
}
